import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash";

import { queryClient } from "App";
import UserService from "services/User";
import { addResizedColumns } from "./useTableConfig";
import {
  getTwilioPhoneNumbersTableColumns,
  getTwilioPhoneNumbersTableFilters,
} from "routes/twilio_phone_numbers/components/TwilioPhoneNumbersTableColumnsAndFilters";
import { TWILIO_PHONE_NUMBERS_TABLE_CONSTANTS } from "routes/twilio_phone_numbers/TwilioPhoneNumbersTableV3";

/**
 * Invalidate the Twilio Phone Numbers table query to trigger a refetch.
 *
 * @returns {Promise<void>}
 */
export const invalidateTwilioPhoneNumbersTableQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [TWILIO_PHONE_NUMBERS_TABLE_CONSTANTS.queryKey],
  });
};

/**
 * Custom hook for Twilio Phone Numbers table configuration.
 *
 * Fetches and configures table settings including columns and filters.
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.tableName - Name of the table for settings lookup
 * @param {Function} params.handleAssignAgents - Handler for assign agents action
 * @param {Function} params.handleAssignVoicemail - Handler for assign voicemail action
 * @param {Function} params.handleAssignHoldMessage - Handler for set hold message action
 * @returns {Object} Object containing filters and columns configuration
 */
export const useTwilioPhoneNumbersTable = ({
  tableName = null,
  handleAssignAgents = null,
  handleAssignVoicemail = null,
  handleAssignHoldMessage = null,
}) => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const filters = useMemo(() => {
    return getTwilioPhoneNumbersTableFilters();
  }, []);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];

    const initialColumns = getTwilioPhoneNumbersTableColumns({
      handleAssignAgents,
      handleAssignVoicemail,
      handleAssignHoldMessage,
    });

    const twilioPhoneNumbersTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );

    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: twilioPhoneNumbersTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [
    tableName,
    tableSettings,
    isTableSettingsFetched,
    handleAssignAgents,
    handleAssignVoicemail,
    handleAssignHoldMessage,
  ]);

  return { filters, columns };
};
